import { Grid, GridCellProps, GridColumn, GridNoRecords } from '@progress/kendo-react-grid';
import { FC, useEffect, useState } from 'react';
import { ILocationSubscriptionDetail, ISubscriptionVehicle } from '../../model';
import { locationService } from '../../services';

import styles from './LocationDetails.module.scss';

const DATE_FORMAT = '{0:MM/dd/yyyy HH:mm}';

const formatVehicle = ({ licensePlate, state, country }: ISubscriptionVehicle) => {
  const region = [state, country].filter(Boolean).join(', ');
  return region ? `${licensePlate || '-'} (${region})` : licensePlate || '-';
};

const VehiclesCell: FC<GridCellProps> = ({ dataItem, className, style }) => (
  <td className={className} style={style}>
    <ul className={styles.vehicles}>
      {(dataItem as ILocationSubscriptionDetail).vehicles.map((vehicle, index) => (
        <li key={index}>{formatVehicle(vehicle)}</li>
      ))}
    </ul>
  </td>
);

interface Props {
  locationId: string;
  subscriptionId: number;
}

/**
 * Details (events) of a location subscription, loaded when the subscription row is expanded.
 */
const LocationSubscriptionDetails: FC<Props> = ({ locationId, subscriptionId }) => {
  const [details, setDetails] = useState<ILocationSubscriptionDetail[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isActual = true;
    setIsLoading(true);
    locationService
      .getLocationSubscriptionDetails(locationId, subscriptionId)
      .then((response) => isActual && setDetails(response))
      // the error is shown by the global API error handler
      .catch(() => isActual && setDetails([]))
      .finally(() => isActual && setIsLoading(false));

    return () => {
      isActual = false;
    };
  }, [locationId, subscriptionId]);

  return (
    <Grid data={details} dataItemKey='id' className={styles.subscriptionDetailsGrid}>
      <GridNoRecords>{isLoading ? 'Loading...' : 'No details found for the subscription.'}</GridNoRecords>
      <GridColumn field='eventTime' title='Event Time' width={150} format={DATE_FORMAT} />
      <GridColumn field='type' title='Type' width={180} />
      <GridColumn field='parkerId' title='Parker ID' />
      <GridColumn field='startTime' title='Start Time' width={150} format={DATE_FORMAT} />
      <GridColumn field='endTime' title='End Time' width={150} format={DATE_FORMAT} />
      <GridColumn field='vehicles' title='Vehicles' cell={VehiclesCell} />
      <GridColumn field='sourceLocationId' title='Source Location ID' width={150} />
    </Grid>
  );
};

export default LocationSubscriptionDetails;
